import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import BackLink from '../components/BackLink';
import { useApi } from '../apiHelpers';
import { parseDate, formatEventDate, getDisplayStatus, businessToday } from '../utils/date-utils';
import LogoSpinner from '../components/ui/LogoSpinner';
import { StatTile } from '../components/ui/Panel';
import EmptyState from '../components/ui/EmptyState';
import FlameIcon from '../components/FlameIcon';
import { getBufferedTotal } from '../utils/drone-utils';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import { useToast } from '../components/Toast/ToastContext';
import PageLayout from '../components/ui/PageLayout';
import { IconButton, buttonClass } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import Tag from '../components/ui/Tag';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';
import { CATEGORY } from '../components/ui/categoryColors';
import Chip from '../components/ui/Chip';
import SegTrack from '../components/ui/SegTrack';
import Select from '../components/ui/Select';

// Pyro indicator — same purple flame used on the calendar, fleet list, and packing
// banner so "purple flame = pyro" reads identically everywhere.
const PyroBadge = () => (
  <Tag hex={CATEGORY.purple.ink.hex} title="This load includes pyro drones" className="flex-shrink-0">
    <span className="inline-flex items-center gap-1">
      <FlameIcon className="w-3 h-3" />
      Pyro
    </span>
  </Tag>
);

// ─── Constants ───────────────────────────────────────────────

// Tag colours: the hue each status carried as a ring pill. 'neutral' is the quiet grey.
const STATUS_TAG = {
  pending:   { tone: 'neutral' },
  confirmed: { hex: CATEGORY.blue.ink.hex },
  packing:   { hex: CATEGORY.purple.ink.hex },
  active:    { hex: CATEGORY.green.ink.hex },
  completed: { hex: CATEGORY.emerald.ink.hex },
  cancelled: { hex: CATEGORY.red.ink.hex },
};

const URGENCY = (days) => {
  if (days < 0)  return { label: 'OVERDUE', dot: 'bg-red-500',    text: 'text-red-400',    glow: 'rgba(248,113,113,0.08)' };
  if (days === 0) return { label: 'TODAY',  dot: 'bg-red-500',    text: 'text-red-400',    glow: 'rgba(248,113,113,0.06)' };
  if (days <= 2) return { label: `${days}d`, dot: 'bg-red-400',    text: 'text-red-400',    glow: 'rgba(248,113,113,0.05)' };
  if (days <= 4) return { label: `${days}d`, dot: 'bg-orange-400', text: 'text-orange-400', glow: null };
  if (days <= 6) return { label: `${days}d`, dot: 'bg-yellow-400', text: 'text-yellow-400', glow: null };
  return              { label: `${days}d`, dot: 'bg-green-400',  text: 'text-green-400',  glow: null };
};

const getTrackingUrl = (num) => {
  if (!num) return null;
  const clean = num.replace(/\s/g, '');
  if (clean.startsWith('1Z')) return `https://www.ups.com/track?tracknum=${encodeURIComponent(clean)}`;
  if (/^\d{12,22}$/.test(clean)) return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(clean)}`;
  if (/^\d{20,}$/.test(clean)) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(clean)}`;
  return `https://parcelsapp.com/en/tracking/${encodeURIComponent(clean)}`;
};

// getBufferedTotal imported from ../utils/drone-utils

// ─── Week Grouping ───────────────────────────────────────────

const getWeekLabel = (dateStr) => {
  const d = parseDate(dateStr);
  if (!d) return 'Unknown';
  const now = businessToday();
  const startOfThisWeek = now.startOf('week');
  const startOfNextWeek = startOfThisWeek.add(7, 'day');
  const startOfWeekAfter = startOfNextWeek.add(7, 'day');

  if (d.isBefore(startOfThisWeek)) return 'Overdue';
  if (d.isBefore(startOfNextWeek)) return 'This Week';
  if (d.isBefore(startOfWeekAfter)) return 'Next Week';
  const weekStart = d.startOf('week');
  return `${weekStart.format('MMM D')} – ${weekStart.add(6, 'day').format('MMM D')}`;
};

// ─── Sub-components ──────────────────────────────────────────

const SectionHeading = ({ children, count, accent = 'bg-primary/60' }) => (
  <div className="flex items-center gap-2.5 mb-1">
    <div className={`w-1 h-3.5 rounded-full ${accent}`} />
    <h2 className="text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold">{children}</h2>
    {count != null && (
      <span className="text-[11px] text-on-surface-variant font-medium tabular-nums">({count})</span>
    )}
  </div>
);

const WeekHeader = ({ label }) => (
  <div className="text-[10px] text-on-surface-variant uppercase tracking-widest font-semibold py-2 mt-3 first:mt-0">
    {label}
  </div>
);

const EmptyColumn = ({ mode, direction }) => {
  const kind = mode === 'shipping' ? 'shipments' : 'drives';
  const headline = direction === 'in_transit'
    ? `No ${kind} in transit`
    : direction === 'return'
      ? `No incoming ${kind}`
      : `No upcoming ${kind}`;
  const sub = direction === 'in_transit'
    ? `Active events with this transport mode will appear here while their gear is out.`
    : `Events with ship dates in this window will appear here`;
  return (
    <EmptyState
      className="mt-3"
      title={headline}
      mark={
        <svg className="w-8 h-8 text-on-surface-disabled" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              {mode === 'shipping' ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 0115 0z" />
              )}
            </svg>
      }
    >
      {sub}
    </EmptyState>
  );
};

// ─── Tour Mini-Timeline ──────────────────────────────────────

const TIMELINE_DOT_COLORS = {
  confirmed: 'bg-blue-400',
  packing:   'bg-purple-400',
  active:    'bg-green-400',
  completed: 'bg-emerald-400',
  pending:   'bg-on-surface-variant',
  cancelled: 'bg-red-400',
};

const TourTimeline = ({ tourEvents }) => {
  if (!tourEvents || tourEvents.length <= 1) return null;
  return (
    <div className="flex items-center gap-1 mt-2 overflow-x-auto pb-1">
      {tourEvents.map((ev, i) => (
        <React.Fragment key={ev.id}>
          {i > 0 && <div className="w-4 h-px bg-outline-variant flex-shrink-0" />}
          <div className="flex flex-col items-center flex-shrink-0">
            <div className={`w-2 h-2 rounded-full ${TIMELINE_DOT_COLORS[getDisplayStatus(ev)] || 'bg-on-surface-variant'}`} />
            <span className="text-[9px] text-on-surface-variant mt-0.5 max-w-[60px] truncate">{ev.name}</span>
            <span className="text-[9px] text-on-surface-variant">{formatEventDate(ev.event_date, 'M/D')}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

// ─── Dispatch Card ──────────────────────────────────────────

const DispatchCard = ({ item, direction, index, onReturn }) => {
  const history = useHistory();
  const [expanded, setExpanded] = useState(false);
  const [returning, setReturning] = useState(false);
  const confirm = useConfirm();

  // In transit: gear is out, next milestone is the return — so treat
  // ship_back_date as the primary date, same as the return direction.
  const usesShipBack = direction === 'return' || direction === 'in_transit';
  const relevantDate = usesShipBack
    ? (item._tourShipBack || item.ship_back_date)
    : (item._tourShipOut || item.ship_out_date);
  const otherDate = usesShipBack
    ? (item._tourShipOut || item.ship_out_date)
    : (item._tourShipBack || item.ship_back_date);

  const daysAway = parseDate(relevantDate)?.diff(businessToday(), 'day') ?? 999;
  const totalDrones = getBufferedTotal(item);
  const isTour = !!item._tourEvents;

  // Tours are packed ONCE at the start of the tour, not between stops. Once any
  // stop is active/completed, the gear has already left — "OVERDUE" is wrong.
  // Same for standalone events whose status is active (gear is in transit).
  const displayStatus = getDisplayStatus(item);
  const tourInTransit = isTour && item._tourEvents.some(e => ['active', 'completed'].includes(e.status));
  const inTransit = direction === 'in_transit' || displayStatus === 'active' || displayStatus === 'completed' || tourInTransit;

  const urgency = inTransit
    ? { label: 'IN TRANSIT', dot: 'bg-green-500', text: 'text-green-400', glow: null }
    : URGENCY(daysAway);

  // ─── Can this card be returned, and how much does one click close? ───
  //
  // A tour card is ONE representative row: the first stop, or the LAST one on the
  // Returns tab. Reading item.status alone therefore made the button appear or
  // vanish depending on which tab you were looking at, and two of the tours in the
  // DB carry mixed stop statuses (active stops with a confirmed one), so that was
  // not hypothetical. Read the stop list instead.
  const activeStops = isTour
    ? item._tourEvents.filter(e => e.status === 'active')
    : (item.status === 'active' ? [item] : []);

  // Gear ships before the show, so an event is 'active' with its show still ahead
  // for the whole ship-out window. The server refuses a return then (it would free
  // drones that are on a truck), so do not offer the click. _tourEvents carries no
  // end_date, so this is event_date-only and deliberately approximate: the server
  // holds the precise COALESCE(end_date, event_date) rule.
  const startOfToday = businessToday();
  const stillToFly = isTour
    ? item._tourEvents.filter(e => (
      e.status !== 'cancelled' && parseDate(e.event_date)?.isAfter(startOfToday)
    )).length
    : (parseDate(item.end_date || item.event_date)?.isAfter(startOfToday) ? 1 : 0);

  const canReturn = activeStops.length > 0 && stillToFly === 0;

  const trackingNum = direction === 'return' ? item.tracking_number_return : item.tracking_number_out;
  const trackingUrl = getTrackingUrl(trackingNum);

  // Ship-out day hint
  const shipOutDays = parseDate(item.event_date)?.diff(parseDate(item._tourShipOut || item.ship_out_date), 'day');
  const shipBackDays = parseDate(item._tourShipBack || item.ship_back_date)?.diff(parseDate(item.end_date || item.event_date), 'day');

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  // A tour card stands for the whole tour's return leg, so the dialog says so out
  // loud rather than letting one click quietly close four shows. It counts ACTIVE
  // stops, not _tourEventCount: the server only closes stops that are out, so
  // promising "all 9 stops" when 4 will move is a lie the toast then contradicts.
  const handleReturn = async () => {
    if (returning) return;
    const n = activeStops.length;
    const ok = await confirm({
      title: `Return ${isTour ? (item.tour_name || 'this tour') : item.name}?`,
      message: isTour
        ? `Marks the gear as back at the warehouse. ${n} stop${n === 1 ? '' : 's'} on this tour close out.`
        : 'Marks the gear as back at the warehouse and closes this show.',
      confirmLabel: 'Mark returned',
    });
    if (!ok) return;
    setReturning(true);
    // Resolves false on failure; the card stays mounted, so hand the button back.
    const closed = await onReturn(item);
    if (!closed) setReturning(false);
  };

  return (
    <div
      onClick={() => {
        const dest = (item.status === 'confirmed' || item.status === 'packing') ? `/packing/${item.id}` : `/events/${item.id}`;
        history.push(dest);
      }}
      className={`bg-surface-container rounded-panel p-4 transition-all duration-200 hover:translate-y-[-1px] cursor-pointer
        ${isTour ? 'border-l-2 border-l-cyan-500/40' : ''}
        ${urgency.label === 'OVERDUE' ? 'ring-1 ring-inset ring-red-500/40 animate-pulse' : ''}`}
      style={{
        boxShadow: urgency.glow ? `0 0 20px ${urgency.glow}` : undefined,
        animation: `fadeInUp 0.3s ease-out ${index * 50}ms both`,
      }}
    >
      {/* Row 1: Name + Status + Days */}
      <div className="flex items-start gap-2">
        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${urgency.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/events/${item.id}`} className="text-sm text-white font-medium truncate hover:text-primary transition-colors">
              {item.name}
            </Link>
            <Tag hex={CATEGORY.sky.ink.hex} size="sm" className="flex-shrink-0">Event</Tag>
            <Tag {...(STATUS_TAG[getDisplayStatus(item)] || {})} size="sm" className="flex-shrink-0 capitalize">
              {getDisplayStatus(item)}
            </Tag>
            {item.pyro_drone_count > 0 && <PyroBadge />}
          </div>

          {/* Tour badge */}
          {isTour && (
            <Link to={`/events/tours/${item.tour_id}`} onClick={e => e.stopPropagation()}
              className="inline-flex items-center gap-1 mt-1 text-[10px] text-cyan-400 hover:text-cyan-300 transition-colors">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.07-9.07a4.5 4.5 0 00-6.364 0l-4.5 4.5a4.5 4.5 0 006.364 6.364l1.757-1.757" />
              </svg>
              {item.tour_name || 'Tour'} ({item._tourEventCount} stops)
            </Link>
          )}

          {/* Location */}
          {item.location && (
            <p className="text-[11px] text-on-surface-variant mt-0.5 truncate">{item.location}</p>
          )}
        </div>

        {/* Days countdown */}
        <div className={`text-sm font-bold tabular-nums flex-shrink-0 ${urgency.text}`}>
          {urgency.label}
        </div>
      </div>

      {/* Row 2: Dates + Drones */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2.5 text-[11px] text-on-surface-variant">
        <span>
          {usesShipBack ? 'Back' : 'Out'}: <span className="text-on-surface">{formatEventDate(relevantDate, 'MMM D')}</span>
          {direction === 'outbound' && shipOutDays != null && (
            <span className="text-on-surface-variant ml-1">({shipOutDays}d before)</span>
          )}
          {direction === 'return' && shipBackDays != null && (
            <span className="text-on-surface-variant ml-1">({shipBackDays}d after)</span>
          )}
          {direction === 'in_transit' && daysAway >= 0 && (
            <span className="text-on-surface-variant ml-1">(in {daysAway}d)</span>
          )}
          {direction === 'in_transit' && daysAway < 0 && (
            <span className="text-red-400 ml-1">({Math.abs(daysAway)}d overdue)</span>
          )}
        </span>
        <span>
          {usesShipBack ? 'Out' : 'Back'}: <span className="text-on-surface">{formatEventDate(otherDate, 'MMM D')}</span>
        </span>
        <span>
          <span className="text-on-surface">{totalDrones}</span> drones
        </span>
      </div>

      {/* Row 3: Tracking */}
      {trackingNum && (
        <div className="flex items-center gap-2 mt-2 text-[11px]" onClick={e => e.stopPropagation()}>
          <span className="text-on-surface-variant">Tracking:</span>
          {trackingUrl ? (
            <a href={trackingUrl} target="_blank" rel="noopener noreferrer"
              className="text-primary hover:text-primary/80 underline underline-offset-2 inline-flex items-center gap-1 truncate max-w-[180px]">
              {trackingNum}
              <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </a>
          ) : (
            <span className="text-on-surface truncate max-w-[180px]">{trackingNum}</span>
          )}
          <Tooltip label="Copy tracking number"><IconButton label="Copy tracking number" size="sm" onClick={() => handleCopy(trackingNum)} className="flex-shrink-0">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
            </svg>
          </IconButton></Tooltip>
        </div>
      )}

      {/* Row 4: Quick action + Tour expand */}
      <div className="flex items-center justify-between mt-2.5" onClick={e => e.stopPropagation()}>
        {/* Tour expand toggle */}
        {isTour && (
          <button onClick={() => setExpanded(!expanded)}
            className="text-[10px] text-on-surface-variant hover:text-on-surface transition-colors inline-flex items-center gap-1">
            <svg className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
            {expanded ? 'Hide' : 'Show'} stops
          </button>
        )}

        {/* Pack & Ship link */}
        {(item.status === 'confirmed' || item.status === 'packing') && direction !== 'return' && (
          <Link to={`/packing/${item.id}`}
            className={`ml-auto ${buttonClass('purple', 'sm')}`}
          >
            Pack & Ship
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </Link>
        )}

        {/* Return — the closing move on the transport leg. Same return arrow as the
            Pending Returns stat card, so the glyph means one thing on this page. */}
        {canReturn && onReturn && (
          <span className="ml-auto">
          <Tooltip label={`Mark the gear from ${isTour ? (item.tour_name || 'this tour') : item.name} as returned`}>
          <button onClick={handleReturn} disabled={returning}
            className={buttonClass('good', 'sm')}
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
            </svg>
            {returning ? 'Returning' : 'Return'}
          </button>
          </Tooltip>
          </span>
        )}
      </div>

      {/* Tour timeline expand */}
      {isTour && <Reveal show={expanded}><TourTimeline tourEvents={item._tourEvents} /></Reveal>}
    </div>
  );
};

// ─── Filter Bar ──────────────────────────────────────────────

const STATUS_FILTERS = [
  { key: 'confirmed', label: 'Confirmed', hex: CATEGORY.blue.ink.hex },
  { key: 'packing',   label: 'Packing',   hex: CATEGORY.purple.ink.hex },
  { key: 'active',    label: 'Active',    hex: CATEGORY.green.ink.hex },
];

const LOOKAHEAD_OPTIONS = [14, 30, 60, 90];

const FilterBar = ({ filters, setFilters }) => {
  const toggleStatus = (status) => {
    setFilters(f => {
      const set = new Set(f.statuses);
      if (set.has(status)) { set.delete(status); } else { set.add(status); }
      if (set.size === 0) set.add('confirmed'); // at least one
      return { ...f, statuses: [...set] };
    });
  };

  const toggleSource = (sourceKey) => {
    setFilters(f => {
      const next = { ...f, [sourceKey]: !f[sourceKey] };
      // At least one source must stay on
      if (!next.showEvents && !next.showOrders) return f;
      return next;
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Source toggles — Events | Orders */}
      <SegTrack
        multi
        size="sm"
        label="Sources"
        options={[{ key: 'showEvents', label: 'Events' }, { key: 'showOrders', label: 'Orders' }]}
        value={[filters.showEvents && 'showEvents', filters.showOrders && 'showOrders'].filter(Boolean)}
        onChange={toggleSource}
      />

      <div className="w-px h-4 bg-gray-700/50 mx-1" />

      {/* Status toggles (event-scoped). Disabled in In Transit (status = active is implicit). */}
      {STATUS_FILTERS.map(({ key, label, hex }) => {
        const active = filters.statuses.includes(key);
        const disabled = !filters.showEvents || filters.direction === 'in_transit';
        return (
          <Chip key={key} pressed={active && !disabled} hex={hex} onClick={() => toggleStatus(key)} disabled={disabled}>
            {label}
          </Chip>
        );
      })}

      <div className="w-px h-4 bg-gray-700/50 mx-1" />

      {/* Direction toggle — Outbound | In Transit | Returns */}
      <SegTrack
        size="sm"
        label="Direction"
        options={[
          { key: 'outbound',   label: 'Outbound' },
          { key: 'in_transit', label: 'In Transit' },
          { key: 'return',     label: 'Returns' },
        ]}
        value={filters.direction}
        onChange={key => setFilters(f => ({ ...f, direction: key }))}
      />

      <div className="w-px h-4 bg-gray-700/50 mx-1" />

      {/* Lookahead — n/a for In Transit (no horizon; gear is already out) */}
      <Select
        label="Look ahead"
        size="sm"
        width="fit"
        value={String(filters.daysAhead)}
        onChange={key => setFilters(f => ({ ...f, daysAhead: parseInt(key) }))}
        disabled={filters.direction === 'in_transit'}
        options={LOOKAHEAD_OPTIONS.map(d => ({ key: String(d), label: `${d} days` }))}
      />
    </div>
  );
};

// ─── Column Component ────────────────────────────────────────

// Mixed-feed week grouping: interleaves events and orders by their
// relevant date so ops sees everything going out in a given week together.
const groupMixedByWeek = (events, orders, direction) => {
  const groups = new Map();
  const pushInto = (label, entry) => {
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(entry);
  };

  // In Transit groups by ship_back_date — return is the next milestone,
  // so "This Week" surfaces gear returning this week.
  const usesShipBack = direction === 'return' || direction === 'in_transit';
  events.forEach(event => {
    const dateField = usesShipBack
      ? (event._tourShipBack || event.ship_back_date)
      : (event._tourShipOut || event.ship_out_date);
    pushInto(getWeekLabel(dateField), { _kind: 'event', _date: dateField, item: event });
  });
  orders.forEach(order => {
    // Use effective_ship_date (server-computed: real ship_date OR arrival - 3 days)
    const orderDate = order.effective_ship_date || order.ship_date;
    pushInto(getWeekLabel(orderDate), { _kind: 'order', _date: orderDate, item: order });
  });

  // Chronological within each week
  groups.forEach(arr => arr.sort((a, b) => String(a._date || '').localeCompare(String(b._date || ''))));

  return groups;
};

// Stable references for "no items / no orders" cases — keeps useMemo
// deps from invalidating every render when a source is toggled off.
const EMPTY_EVENTS = [];
const EMPTY_ORDERS = [];

const DispatchColumn = ({ items, orders = EMPTY_ORDERS, mode, direction, onReturn }) => {
  const isShipping = mode === 'shipping';
  const label = direction === 'return'
    ? (isShipping ? 'Incoming Shipments' : 'Incoming Drives')
    : direction === 'in_transit'
      ? (isShipping ? 'Shipments In Transit' : 'Drives In Transit')
      : (isShipping ? 'Upcoming Shipments' : 'Upcoming Drives');
  const accent = isShipping ? 'bg-purple-500/60' : 'bg-sky-500/60';

  // Orders only ever appear in the outbound shipping column
  const includeOrders = isShipping && direction !== 'return';
  const orderItems = includeOrders ? orders : EMPTY_ORDERS;

  const eventDrones = items.reduce((sum, item) => sum + getBufferedTotal(item), 0);
  const orderDrones = orderItems.reduce((sum, o) => sum + summarizeOrderLines(o.lines).drones, 0);
  const totalDrones = eventDrones + orderDrones;
  const totalCards = items.length + orderItems.length;

  const weekGroups = useMemo(() => groupMixedByWeek(items, orderItems, direction), [items, orderItems, direction]);

  // Count summary sub-line (events + orders)
  const summaryBits = [];
  if (items.length > 0) {
    const noun = isShipping ? 'shipment' : 'drive';
    summaryBits.push(`${items.length} ${noun}${items.length !== 1 ? 's' : ''}`);
  }
  if (orderItems.length > 0) summaryBits.push(`${orderItems.length} order${orderItems.length !== 1 ? 's' : ''}`);
  if (totalDrones > 0) summaryBits.push(`${totalDrones} drones total`);

  return (
    <div>
      <SectionHeading count={totalCards} accent={accent}>{label}</SectionHeading>
      {totalCards > 0 && summaryBits.length > 0 && (
        <p className="text-xs text-on-surface-variant mb-3">{summaryBits.join(' · ')}</p>
      )}

      {totalCards === 0 ? (
        <EmptyColumn mode={mode} direction={direction} />
      ) : (
        <div className="space-y-2">
          {[...weekGroups.entries()].map(([weekLabel, weekEntries]) => (
            <React.Fragment key={weekLabel}>
              <WeekHeader label={weekLabel} />
              {weekEntries.map((entry, i) => (
                entry._kind === 'order'
                  ? <OrderDispatchCard key={`o-${entry.item.id}`} order={entry.item} index={i} />
                  : <DispatchCard key={`e-${entry.item.id}`} item={entry.item} direction={direction} index={i} onReturn={onReturn} />
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Order Dispatch Card ──────────────────────────────────────

const ORDER_TYPE_TAG = {
  sale:   { tone: 'accent' },
  lease:  { hex: CATEGORY.blue.ink.hex },
  rental: { hex: CATEGORY.orange.ink.hex },
};

const ORDER_STATUS_TAG = {
  pending:          { tone: 'neutral' },
  confirmed:        { hex: CATEGORY.blue.ink.hex },
  in_production:    { hex: CATEGORY.orange.ink.hex },
  ready:            { hex: CATEGORY.green.ink.hex },
  shipping:         { hex: CATEGORY.yellow.ink.hex },
  shipped:          { hex: CATEGORY.purple.ink.hex },
  delivered:        { hex: CATEGORY.green.ink.hex },
  awaiting_return:  { hex: CATEGORY.orange.ink.hex },
};

const summarizeOrderLines = (lines = []) => {
  const sums = { drones: 0, pyro: 0, batteries: 0, ground_stations: 0, chargers: 0, gear_pelican: 0, drone_carrier: 0 };
  lines.forEach(l => {
    const q = parseInt(l.quantity, 10) || 0;
    if (l.item_type === 'drone_pyro') sums.pyro += q;
    if (l.item_type === 'drone_ls' || l.item_type === 'drone_pyro') sums.drones += q;
    else if (l.item_type === 'battery') sums.batteries += q;
    else if (l.item_type === 'ground_station') sums.ground_stations += q;
    else if (l.item_type === 'charger') sums.chargers += q;
    else if (l.item_type === 'gear_pelican') sums.gear_pelican += q;
    else if (l.item_type === 'drone_carrier') sums.drone_carrier += q;
  });
  return sums;
};

const OrderDispatchCard = ({ order, index }) => {
  const history = useHistory();
  // Use server-computed effective_ship_date (real ship_date OR inferred = arrival - 3 days).
  const shipDateForUrgency = order.effective_ship_date || order.ship_date;
  const daysAway = parseDate(shipDateForUrgency)?.diff(businessToday(), 'day') ?? 999;
  const urgency = URGENCY(daysAway);
  const typeTag = ORDER_TYPE_TAG[order.order_type] || { tone: 'neutral' };
  const statusTag = ORDER_STATUS_TAG[order.status] || { tone: 'neutral' };
  const sums = summarizeOrderLines(order.lines);
  const inferred = order.ship_date_inferred === true;
  const arriveBy = order.effective_date || order.delivery_date;

  const lineChips = [];
  if (sums.drones) lineChips.push(`${sums.drones} drones`);
  if (sums.batteries) lineChips.push(`${sums.batteries} batteries`);
  if (sums.ground_stations) lineChips.push(`${sums.ground_stations} GS`);
  if (sums.chargers) lineChips.push(`${sums.chargers} chargers`);
  if (sums.gear_pelican) lineChips.push(`${sums.gear_pelican} pelican`);
  if (sums.drone_carrier) lineChips.push(`${sums.drone_carrier} carrier`);

  return (
    <div
      onClick={() => history.push(`/orders/${order.id}`)}
      className={`bg-surface-container rounded-panel p-4 transition-all duration-200 hover:translate-y-[-1px] cursor-pointer
        border-l-2 border-l-primary/40
        ${urgency.label === 'OVERDUE' ? 'ring-1 ring-inset ring-red-500/40 animate-pulse' : ''}`}
      style={{
        boxShadow: urgency.glow ? `0 0 20px ${urgency.glow}` : undefined,
        animation: `fadeInUp 0.3s ease-out ${index * 50}ms both`,
      }}
    >
      {/* Row 1: Customer + badges + days (parallels how event cards lead with event name) */}
      <div className="flex items-start gap-2">
        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${urgency.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/orders/${order.id}`} onClick={e => e.stopPropagation()}
              className="text-sm text-white font-medium hover:text-primary transition-colors truncate">
              {order.org_name || order.customer_name || '—'}
            </Link>
            <Tag {...typeTag} size="sm" className="flex-shrink-0 capitalize">
              {order.order_type}
            </Tag>
            <Tag {...statusTag} size="sm" className="flex-shrink-0 capitalize">
              {order.status?.replace(/_/g, ' ')}
            </Tag>
            {sums.pyro > 0 && <PyroBadge />}
          </div>
          <Link to={`/orders/${order.id}`} onClick={e => e.stopPropagation()}
            className="text-[11px] text-on-surface-variant hover:text-primary/70 transition-colors mt-0.5 font-mono inline-block">
            {order.so_number}
          </Link>
        </div>
        <div className={`text-sm font-bold tabular-nums flex-shrink-0 ${urgency.text}`}>
          {urgency.label}
        </div>
      </div>

      {/* Row 2: Dates + line summary */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2.5 text-[11px] text-on-surface-variant">
        <span>
          Ship: <span className={inferred ? 'text-amber-400 italic' : 'text-on-surface'}>
            {formatEventDate(shipDateForUrgency, 'MMM D')}{inferred ? ' (inferred)' : ''}
          </span>
        </span>
        {order.delivery_date && !inferred && (
          <span>
            Delivery: <span className="text-on-surface">{formatEventDate(order.delivery_date, 'MMM D')}</span>
          </span>
        )}
        {order.effective_date && !order.delivery_date && !inferred && (
          <span>
            Effective: <span className="text-on-surface">{formatEventDate(order.effective_date, 'MMM D')}</span>
          </span>
        )}
        {lineChips.length > 0 && (
          <span className="text-on-surface-variant truncate">
            {lineChips.join(' · ')}
          </span>
        )}
      </div>

      {/* Inferred-ship warning banner */}
      {inferred && arriveBy && (
        <div className="mt-2 inline-flex items-center rounded-control bg-amber-500/5 px-2 py-1 text-[10px] text-amber-300">
          No ship date set. Needs to arrive by&nbsp;<span className="font-semibold">{formatEventDate(arriveBy, 'MMM D')}</span>
        </div>
      )}

      {/* Row 3: Fulfillment quick link */}
      <div className="flex items-center justify-end mt-2.5" onClick={e => e.stopPropagation()}>
        <Link to={`/orders/${order.id}/fulfillment`}
          className={buttonClass('purple', 'sm')}>
          Fulfillment
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </Link>
      </div>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────

const DIRECTIONS = ['outbound', 'in_transit', 'return'];

const DispatchPage = () => {
  const api = useApi();
  const toast = useToast();
  const location = useLocation();
  const history = useHistory();
  const [stats, setStats] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [drives, setDrives] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const urlDirection = useMemo(() => {
    const d = new URLSearchParams(location.search).get('direction');
    return DIRECTIONS.includes(d) ? d : null;
  }, [location.search]);

  const [filters, setFilters] = useState({
    // 'active' is in the default set on purpose. Without it the Returns tab lists
    // gear that has NOT shipped yet (confirmed/packing) keyed on a planned return
    // date, while the gear genuinely coming back is 'active' and never appears —
    // which is why In Transit was the only place a return could be found.
    // Outbound is unaffected: an active event's ship_out_date is already past, so
    // it fails that tab's `ship_out_date >= today` window.
    statuses: ['confirmed', 'packing', 'active'],
    direction: urlDirection || 'outbound',
    daysAhead: 30,
    showEvents: true,
    showOrders: true,
  });

  // The nav badge deep-links to ?direction=in_transit. Initial state alone is not
  // enough: if you are already on /dispatch, the push changes the search string
  // without remounting, and the bell would drop you on Outbound, pointing at work
  // it just told you about without showing it.
  useEffect(() => {
    if (!urlDirection) return;
    setFilters(f => (f.direction === urlDirection ? f : { ...f, direction: urlDirection }));
  }, [urlDirection]);

  // ...and keep the URL in step with the tab, or the bell goes dead after one use.
  // Pushing an identical search string is not a location change, so if the URL
  // froze at whatever the bell last pushed, a user who then clicked another tab
  // would find the bell silently doing nothing. Replace rather than push: tab
  // changes are not history the back button should walk through.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('direction') === filters.direction) return;
    params.set('direction', filters.direction);
    history.replace({ search: `?${params.toString()}` });
  }, [filters.direction, location.search, history]);

  const statusParam = filters.statuses.join(',');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Orders "outgoing" statuses — roughly the in-flight part of the order lifecycle
      const orderStatuses = 'confirmed,in_production,ready,shipping';
      const wantEvents = filters.showEvents;
      const wantOrders = filters.showOrders && filters.direction === 'outbound';

      const [statsData, shipData, driveData, orderData] = await Promise.all([
        api.get('/events/logistics-stats', { silent: true }),
        wantEvents
          ? api.get(`/events/shipping-schedule?mode=shipping&status=${statusParam}&days=${filters.daysAhead}&direction=${filters.direction}`, { silent: true })
          : Promise.resolve([]),
        wantEvents
          ? api.get(`/events/shipping-schedule?mode=driving&status=${statusParam}&days=${filters.daysAhead}&direction=${filters.direction}`, { silent: true })
          : Promise.resolve([]),
        wantOrders
          ? api.get(`/orders/dispatch-schedule?status=${orderStatuses}&days=${filters.daysAhead}`, { silent: true })
          : Promise.resolve([]),
      ]);
      setStats(statsData);
      setShipments(shipData || []);
      setDrives(driveData || []);
      setOrders(orderData || []);
    } catch {
      // silent — useApi handles logout
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusParam, filters.direction, filters.daysAhead, filters.showEvents, filters.showOrders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Gear is back. Resolves true when the row(s) closed, false when the server
  // refused, so the card can hand its button back instead of sitting spinning.
  //
  // Drops the card locally rather than refetching the list: 28 of these were owed
  // the day this shipped, and a full round trip between every click makes a
  // clearing-the-board task feel like work. The stat cards are counts rather than
  // rows, so they refresh quietly in the background.
  const handleReturn = async (item) => {
    try {
      const res = await api.post(`/events/${item.id}/packing/return`, { silent: true });
      // A tour card stands for every stop it collapsed, so drop them all.
      const closed = new Set(item._tourEvents ? item._tourEvents.map(e => e.id) : [item.id]);
      const without = (list) => list.filter(e => !closed.has(e.id));
      setShipments(without);
      setDrives(without);
      toast.success(res?._stopsClosed > 1 ? `Returned ${res._stopsClosed} stops` : 'Marked returned');
      api.get('/events/logistics-stats', { silent: true }).then(setStats).catch(() => {});
      return true;
    } catch (error) {
      // The 409s carry a real explanation ("3 stops still upcoming"), so show it
      // rather than a generic failure.
      toast.error(error.message || 'Could not mark this returned');
      return false;
    }
  };

  return (
    <PageLayout width="wide" kind="workspace" className="pt-6 space-y-6">
      {/* Page header */}
      <div>
        <BackLink to="/events" label="Events" className="mb-2" />
        <h1 className="text-lg font-semibold text-white tracking-tight">Dispatch</h1>
      </div>

      {/* Zone 1: Hero Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile
          label="Active Shipments"
          tone="purple"
          size="lg"
          lit
          value={stats?.activeShipments ?? '–'}
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
            </svg>
          }
        />
        <StatTile
          label="Active Drives"
          tone="blue"
          size="lg"
          lit
          value={stats?.activeDrives ?? '–'}
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 0115 0z" />
            </svg>
          }
        />
        <StatTile
          label="Pending Outbound"
          tone="orange"
          size="lg"
          lit
          value={stats?.pendingOutbound ?? '–'}
          sub={stats?.redZoneCount > 0 ? (
            <span className="inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
              {stats.redZoneCount} in red zone
            </span>
          ) : null}
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <StatTile
          label="Pending Returns"
          tone="green"
          size="lg"
          lit
          value={stats?.pendingReturns ?? '–'}
          // pendingReturns is forward-bounded (next 14 days), so it read 0 while 46
          // events sat overdue. The sub-line carries the owed count in the same
          // shape Pending Outbound uses for its red zone, so a lit nav badge and
          // this card can never disagree.
          sub={stats?.returnsDue > 0 ? (
            <span className="inline-flex items-center gap-1 text-red-400">
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse motion-reduce:animate-none" />
              {stats.returnsDue} due back
            </span>
          ) : null}
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
            </svg>
          }
        />
      </div>

      {/* Zone 2: Filter Bar */}
      <FilterBar filters={filters} setFilters={setFilters} />

      {/* Zone 3: Column layout driven by Events / Orders toggles.
          - Shipping column shows events (if Events on) mixed with orders (if Orders on + outbound).
          - Drives column is event-only — hidden when Events is off. */}
      {(() => {
        if (loading && !shipments.length && !drives.length && !orders.length) {
          return <LogoSpinner label="Loading dispatch" />;
        }
        const ordersInShipColumn = filters.showOrders && filters.direction === 'outbound';
        const showShipColumn = filters.showEvents || ordersInShipColumn;
        const showDriveColumn = filters.showEvents;
        const twoColumns = showShipColumn && showDriveColumn;

        if (!showShipColumn && !showDriveColumn) {
          return (
            <FadeIn>
              <EmptyState variant="area" className="py-16">
                Enable Events or Orders above to see dispatch data.
              </EmptyState>
            </FadeIn>
          );
        }

        return (
          <FadeIn>
            <div className={`grid grid-cols-1 gap-6 ${twoColumns ? 'lg:grid-cols-2' : ''}`}>
            {showShipColumn && (
              <DispatchColumn
                items={filters.showEvents ? shipments : EMPTY_EVENTS}
                orders={ordersInShipColumn ? orders : EMPTY_ORDERS}
                mode="shipping"
                direction={filters.direction}
                onReturn={handleReturn}
              />
            )}
            {showDriveColumn && (
              <DispatchColumn
                items={drives}
                orders={EMPTY_ORDERS}
                mode="driving"
                direction={filters.direction}
                onReturn={handleReturn}
              />
            )}
            </div>
          </FadeIn>
        );
      })()}

      {/* Fade-in animation keyframes */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </PageLayout>
  );
};

export default DispatchPage;
